import { Link } from 'react-router'
import { Text } from '@carouselgg/nexus/text'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import { CircleCheck, CircleHelp } from '@carouselgg/nexus/icon'
import type { TrustStanding } from '../../api/compete'
import styles from './TrustRequirement.module.css'

/**
 * A competition's Trust Score minimum — or the plain fact that it has none —
 * answered for this visitor: their score
 * for the game on a gauge against the minimum, and — if they fall short — a
 * help button with how to close the gap. Shown either way, because a minimum a
 * player only finds out about at sign-up is a refusal they never saw coming.
 */
export function TrustRequirement({ min, gameName, standing, signedIn, signInHref, onHelp }: {
  /** Null or 0: no minimum. Eligibility treats it the same way. */
  min: number | null
  gameName: string
  /** Null while loading, or signed out. */
  standing: TrustStanding | null
  signedIn: boolean
  /** Where "Sign in to check" goes, back to this page. */
  signInHref: string
  onHelp: () => void
}) {
  if (!min) {
    return (
      <div>
        <Text size="xs" muted className={styles.block}>Minimum Trust Score</Text>
        <span className={`${styles.row} ${styles.figure}`}>None</span>
        <Text size="xs" muted className={styles.block}>Open to any Trust Score</Text>
      </div>
    )
  }
  const short = standing != null && standing.score < min
  const whose = standing?.scope === 'game' ? `Your ${gameName} Trust Score` : 'Your Trust Score'

  return (
    <div>
      <Text size="xs" muted className={styles.block}>Minimum Trust Score</Text>
      <div className={styles.row}>
        <span className={styles.figure}>{min}</span>
        {/* Theirs beside the minimum: the ring turns danger-coloured below it. */}
        {standing && <TrustGauge score={standing.score} minRequired={min} size={36} />}
        {short && (
          <button type="button" className={styles.help} onClick={onHelp} aria-label={`How to reach a Trust Score of ${min}`}>
            <CircleHelp size={18} />
          </button>
        )}
      </div>
      {!signedIn ? (
        <Text size="xs" muted className={styles.block}>
          <Link to={signInHref} className={styles.link}>Sign in</Link> to see if you qualify
        </Text>
      ) : !standing ? (
        <Text size="xs" muted className={styles.block}>Checking your score…</Text>
      ) : short ? (
        <Text size="xs" className={`${styles.block} ${styles.short}`}>
          {whose} is {standing.score}, {min - standing.score} short.{' '}
          <button type="button" className={styles.linkButton} onClick={onHelp}>How to get there</button>
        </Text>
      ) : (
        <Text size="xs" muted className={`${styles.block} ${styles.ok}`}>
          <CircleCheck size={12} /> {whose} is {standing.score}. You qualify.
        </Text>
      )}
    </div>
  )
}
